import { useCallback, useRef, useState } from "react";

/** Gestion d'un champ fichier avec aperçu et suppression (backoffice). */
const useFileField = () => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [existingUrl, setExistingUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [removed, setRemoved] = useState(false);

  const handleChange = useCallback((e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;
    setFile(selected);
    setPreviewUrl(URL.createObjectURL(selected));
    setRemoved(false);
  }, []);

  const handleRemove = useCallback(() => {
    setFile(null);
    setPreviewUrl("");
    setExistingUrl("");
    setRemoved(true);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const loadExisting = useCallback((url) => {
    setExistingUrl(url || "");
    setFile(null);
    setPreviewUrl("");
    setRemoved(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const reset = useCallback(() => {
    setFile(null);
    setExistingUrl("");
    setPreviewUrl("");
    setRemoved(false);
    if (inputRef.current) inputRef.current.value = "";
  }, []);

  const previewSrc = removed ? "" : previewUrl || existingUrl || "";
  const fileName =
    file?.name ||
    (existingUrl && !removed ? existingUrl.split("/").pop() : "");
  const hasValue = Boolean(file || (existingUrl && !removed));
  const hasPreview = Boolean(previewSrc);
  const isImageFile =
    file?.type?.startsWith("image/") ||
    (!file &&
      existingUrl &&
      /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(existingUrl));

  const appendToFormData = useCallback(
    (formData, fieldName, removeFieldName) => {
      if (file instanceof File) {
        formData.append(fieldName, file);
      } else if (removed && removeFieldName) {
        formData.append(removeFieldName, "1");
      }
    },
    [file, removed]
  );

  const getFetchUrl = useCallback(() => {
    if (removed) return null;
    if (previewUrl) return previewUrl;
    if (existingUrl && typeof existingUrl === "string") return existingUrl;
    return null;
  }, [removed, previewUrl, existingUrl]);

  return {
    inputRef,
    file,
    existingUrl,
    previewUrl,
    removed,
    handleChange,
    handleRemove,
    loadExisting,
    reset,
    previewSrc,
    fileName,
    hasValue,
    hasPreview,
    isImageFile,
    appendToFormData,
    getFetchUrl,
  };
};

export default useFileField;
